import React, { useState, useEffect } from "react";
import { Input } from "components/Graphs/Input";
import FlatCard from "components/UI-lib/FlatCard";
import {
  Upload,
  Code,
  FileCode,
  GitBranch,
  LucideLightbulb,
} from "lucide-react";
import { motion } from "framer-motion";
import { cx } from "components/Graphs/utils";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import { FadeInDiv } from "components/Motion/FadeInDiv";
import {
  BellAlertIcon,
  BookOpenIcon,
  CodeBracketIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import { Button as OldButton } from "components/UI-lib";
interface QuickStartConfigurationProps {
  codeSource: string;
  onCodeSourceChange: (source: string) => void;
  jupyterDirectory: string;
  onJupyterDirectoryChange: (directory: string) => void;
}

export const QuickStartConfiguration: React.FC<
  QuickStartConfigurationProps
> = ({
  codeSource,
  onCodeSourceChange,
  jupyterDirectory,
  onJupyterDirectoryChange,
}) => {
  const [activeTab, setActiveTab] = useState<string>("git");

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4 text-primary">
        Quick Start Setup
      </h2>
      <p className="text-secondary mb-6">
        We've pre-configured a VM environment with Jupyter pre-installed. Just
        add your code and configure your Jupyter entry point.
      </p>

      <FlatCard>
        <div className="flex items-center mb-4">
          <CodeBracketIcon className="h-5 w-5 text-primary mr-2" />
          <h2 className="text-primary text-lg font-semibold">
            Add your code files
          </h2>
        </div>
        <Tabs defaultValue="git" onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2" variant="solid">
            <TabsTrigger value="git" className="flex items-center">
              <div className="flex items-center text-primary">
                <GitBranch className="mr-2 h-4 w-4" /> Git URLs
              </div>
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center" disabled>
              <div className="flex items-center text-primary">
                <Upload className="mr-2 h-4 w-4" /> Upload Files (Coming Soon)
              </div>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="git" className="pt-4">
            <FadeInDiv className="space-y-4">
              <div>
                <h3 className="text-primary text-sm mb-1">
                  Enter a public Github/Gitlab URL repository or notebook file
                  URL
                </h3>
                <Input
                  id="repo-url"
                  placeholder="https://"
                  value={activeTab === "git" ? codeSource : ""}
                  onChange={(e) => onCodeSourceChange(e.target.value)}
                />
                <p className="text-xs text-secondary mt-1">
                  Examples: https://github.com/username/repo.git or
                  https://github.com/username/repo/blob/main/notebook.ipynb
                </p>
              </div>
            </FadeInDiv>
          </TabsContent>
          <TabsContent value="upload" className="pt-4">
            <div className="border border-dashed border-secondary rounded-lg p-8 text-center">
              <Upload className="mx-auto h-10 w-10 text-gray-400 mb-2" />
              <p className="text-sm text-secondary mb-2">
                Drag and drop your notebook or your zipped code file
              </p>
              <p className="text-xs text-secondary">
                Supports .ipynb and .zip files
              </p>
              <Input
                type="file"
                className="hidden"
                id="file-upload"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    onCodeSourceChange(e.target.files[0].name);
                  }
                }}
              />
              <label
                htmlFor="file-upload"
                className="text-primary mt-4 inline-block px-4 py-2 bg-highlight rounded-md text-sm cursor-pointer hover:bg-highlightDarker"
              >
                Select Files
              </label>
            </div>
          </TabsContent>
        </Tabs>
      </FlatCard>

      <FlatCard className="mt-6">
        <div className="pb-2">
          <h3 className="text-lg flex items-center text-primary font-semibold">
            <BookOpenIcon className="mr-2 h-5 w-5" />
            Your Preconfigured Jupyter Experience
          </h3>
          <p className="text-secondary text-sm">
            Your quick start environment comes with Jupyter pre-installed on the
            Host
          </p>
        </div>

        <div className="p-3 bg-secondaryBg rounded-md text-sm text-primary">
          <p className="font-medium mb-1">Quick Start Configurations:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Jupyter Lab installed on the host and running on port 8888</li>
            <li>Secure access via Cloudflare Tunnel</li>
          </ul>
        </div>
        <div className="mt-4 flex flex-col justify-center items-center mt-4 p-3 rounded-md bg-secondaryBg">
          <div className="text-sm text-white mb-2">
            The user will see this option at the end of the deployment to gain
            easy access to their notebook:
          </div>
          <div className="flex flex-row items-center opacity-80 mt-2">
            <OldButton
              type="primary"
              label={"Access Notebook"}
              className=""
              onClick={() => {}}
            />
          </div>

          <span className="text-xs text-highlight mt-6">
            <strong>One click access</strong> to your content on the instance.
          </span>
        </div>
        <div className="pt-4"></div>
      </FlatCard>
    </div>
  );
};

export default QuickStartConfiguration;
